import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Brain, MonitorPlay, Handshake, Database, Lock } from 'lucide-react';
interface SlideMoatProps {
  onContinue: () => void;
}
export function SlideMoat({
  onContinue
}: SlideMoatProps) {
  const [stage, setStage] = useState(0);
  const pillars = [{
    icon: <Brain className="w-5 h-5" data-id="element-459" />,
    title: 'Medical Storytelling',
    desc: 'Codified pharma expertise & intelligence',
    tint: 'rose'
  }, {
    icon: <MonitorPlay className="w-5 h-5" data-id="element-460" />,
    title: 'Short Format Interactive Engagement Platform',
    desc: 'Proprietary engagement layer driving measurable outcomes',
    tint: 'teal'
  }, {
    icon: <Handshake className="w-5 h-5" data-id="element-461" />,
    title: 'Pharma Distribution',
    desc: 'Deep enterprise relationships & access',
    tint: 'blue'
  }, {
    icon: <Database className="w-5 h-5" data-id="element-462" />,
    title: 'Behavioural Dataset',
    desc: 'Privacy-safe HCP insights compound performance',
    tint: 'violet'
  }];
  const tintStyles = {
    rose: {
      bg: 'bg-rose-500/5',
      border: 'border-rose-400/30',
      iconBg: 'bg-rose-500/15',
      iconColor: 'text-rose-400'
    },
    teal: {
      bg: 'bg-teal-400/5',
      border: 'border-teal-400/30',
      iconBg: 'bg-teal-400/15',
      iconColor: 'text-teal-400'
    },
    blue: {
      bg: 'bg-blue-500/5',
      border: 'border-blue-400/30',
      iconBg: 'bg-blue-500/15',
      iconColor: 'text-blue-400'
    },
    violet: {
      bg: 'bg-violet-500/5',
      border: 'border-violet-400/30',
      iconBg: 'bg-violet-500/15',
      iconColor: 'text-violet-400'
    }
  } as const;
  useEffect(() => {
    const t1 = setTimeout(() => setStage(1), 300);
    const timers = pillars.map((_, idx) => setTimeout(() => setStage(2 + idx), 800 + idx * 250));
    const t2 = setTimeout(() => setStage(2 + pillars.length), 800 + pillars.length * 250 + 200);
    const t3 = setTimeout(() => setStage(3 + pillars.length), 800 + pillars.length * 250 + 800);
    return () => {
      clearTimeout(t1);
      timers.forEach(clearTimeout);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-12 max-w-7xl mx-auto" data-id="element-463">
      {/* Heading */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: stage >= 1 ? 1 : 0,
      y: stage >= 1 ? 0 : 20
    }} data-id="element-464">
        <h2 className="text-3xl md:text-5xl font-sans font-bold text-white tracking-tight leading-tight" data-id="element-465">
          The Moat
        </h2>
        <p className="text-teal-400 text-base md:text-lg font-medium mt-1" data-id="element-466">
          Codified Expertise + Data Loop
        </p>
      </motion.div>

      <div className="flex-1 flex flex-col justify-center mt-10" data-id="element-467">
        {/* 4 pillar cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10" data-id="element-468">
          {pillars.map((p, idx) => {
          const style = tintStyles[p.tint as keyof typeof tintStyles];
          return <motion.div key={p.title} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: stage >= 2 + idx ? 1 : 0,
            y: stage >= 2 + idx ? 0 : 20
          }} transition={{
            duration: 0.5
          }} className={`${style.bg} border ${style.border} rounded-2xl p-5 flex flex-col`} data-id="element-469">
                <div className={`w-10 h-10 rounded-xl ${style.iconBg} flex items-center justify-center ${style.iconColor} mb-5`} data-id="element-470">
                  {p.icon}
                </div>
                <h3 className="text-base md:text-lg font-bold text-white leading-tight" data-id="element-471">
                  {p.title}
                </h3>
                <p className="text-white/55 text-sm mt-3 leading-snug" data-id="element-472">
                  {p.desc}
                </p>
              </motion.div>;
        })}
        </div>

        {/* Lock + Unbreakable Flywheel */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: stage >= 2 + pillars.length ? 1 : 0,
        y: stage >= 2 + pillars.length ? 0 : 20
      }} className="mt-8 relative" data-id="element-473">
          {/* Lock badge */}
          <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10" data-id="element-474">
            <div className="w-10 h-10 rounded-full bg-teal-400 flex items-center justify-center shadow-[0_0_24px_rgba(0,212,170,0.4)]" data-id="element-475">
              <Lock className="w-4 h-4 text-navy-900" strokeWidth={2.5} data-id="element-476" />
            </div>
          </div>

          <div className="bg-teal-400/5 border border-teal-400/30 rounded-2xl px-8 pt-9 pb-6 text-center" data-id="element-477">
            <h3 className="text-2xl md:text-3xl font-bold text-white" data-id="element-478">
              Unbreakable Flywheel
            </h3>
            <p className="text-teal-300/90 mt-3 max-w-3xl mx-auto leading-relaxed" data-id="element-479">
              Deep medical intelligence + pharma access + behavioral data create
              a{' '}
              <span className="text-teal-400 font-semibold" data-id="element-480">
                moat no broad player can match
              </span>{' '}
              in this regulated vertical.
            </p>
          </div>
        </motion.div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= 3 + pillars.length ? 1 : 0
    }} className="mt-6 flex justify-end" data-id="element-481">
        <Button onClick={onContinue} data-id="element-482">Financials</Button>
      </motion.div>
    </div>;
}